import { Ban, Edit } from 'lucide-react'; // Icons for edit and block actions
import React, { useEffect, useState } from 'react';
import { Button, Container, FormControl, InputGroup } from 'react-bootstrap';
import styled from 'styled-components';
import GenericModal from '../../components/GenericModal';
import GenericTable from '../../components/GenericTable';
import useModalStrip from '../../hooks/global-hooks/useModalStrip';
import { GameConfig } from '../../models/GameConfig';
import { getAllGameConfig, saveGameConfig, toggleGameConfigStatus } from '../../redux/entities/gameEntity';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import AddGameConfigPage from './AddGamesConfigPage';

const PageHeading = styled.h2`
  margin-top: 3rem;
  font-weight: 600;
  color: #4a90e2;
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 1.5rem;
  letter-spacing: 0.5px;
`;

const AddButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;


const SearchBox = styled(InputGroup)`
  max-width: 300px;
  flex-grow: 1;
  margin-right: 15px;
  margin-bottom: 1rem;

  @media (max-width: 576px) {
    margin-right: 0;
    max-width: 100%;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 2rem;
`;

const ActionButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionIcon = styled.div`
  cursor: pointer;
  color: #555;
  font-size: 1.5rem;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }
`;

const GameConfigPage: React.FC = () => {
  const [modalShow, setModalShow] = useState(false);
  const [selectedItem, setSelectedItem] = useState<GameConfig>({} as GameConfig);
  const [searchQuery, setSearchQuery] = useState('');
  const [paginationData, setPaginationData] = useState({
    totalElements: 0,
    currentPage: 0, // Changed to 0 for zero-based indexing
    pageSize: 20
  });

  const dispatch = useAppDispatch();
  const gameConfigs = useAppSelector((state) => state.entities.gameReducer.gameConfigs);
  const { showModalStrip } = useModalStrip();

  // Fetch data when component mounts or pagination changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const offset = paginationData.currentPage;
        await dispatch(getAllGameConfig(offset)).unwrap();
      } catch (err) {
        showModalStrip('danger', 'Failed to load game configurations', 5000);
      }
    };

    fetchData();
  }, [paginationData.currentPage, dispatch, showModalStrip]);

  // Update total elements when gameConfigs changes
  useEffect(() => {
    if (gameConfigs?.totalElements !== undefined) {
      setPaginationData(prev => ({
        ...prev,
        totalElements: gameConfigs.totalElements
      }));
    }
  }, [gameConfigs?.totalElements]);


  const filteredData = gameConfigs?.content?.filter((item) => {
    if (!searchQuery) return true;
    return item?.game?.name?.toLowerCase().includes(searchQuery.toLowerCase());
  }) || [];

  const columns = [
    { header: 'Game Name', field: 'game.name' },
    {
      header: 'Game Type', field: '',
      render: (rowData: GameConfig) => {
        if (rowData.game?.burfiSupported) return 'Barfi';
        if (rowData.game?.digitSupported) return 'Digit';
        if (rowData.game?.singlePattiSupported) return 'Single Patti';
        return '-';
      }
    },
    { header: 'Result Time', field: 'resultTime.resultTime' },
    { header: 'Limit Time Interval', field: 'limitTimeInterval' },
    {
      header: 'Single (Min/ Max)',
      field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.singleMin; // Replace with actual field for Min value
        const max = rowData?.singleMax; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Single Limit (Min/ Max)',
      field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.singleLimitMin; // Replace with actual field for Min value
        const max = rowData?.singleLimitMax; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Panna (Min/ Max)',
      field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.pattiMin; // Replace with actual field for Min value
        const max = rowData?.pattiMax; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Panna Limit (Min/ Max)',
      field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.pattiLimitMin; // Replace with actual field for Min value
        const max = rowData?.pattiLimitMax; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Barfi (Min/Max)', field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.burfiMin;
        const max = rowData?.burfiMax;
        return min && max ? `${min}/${max}` : '-';
      }
    },
    {
      header: 'Barfi Limit (Min/Max)', field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.burfiLimitMin;
        const max = rowData?.burfiLimitMax;
        return min && max ? `${min}/${max}` : '-';
      }
    },
    {
      header: 'Barfi Win Multipliers', field: '',
      render: (rowData: GameConfig) => {
        const multipliers = [
          `FD: ${rowData?.burfiFirstDigitWinMultiplier || '-'}`,
          `LD: ${rowData?.burfiLastDigitWinMultiplier || '-'}`,
          `EP: ${rowData?.burfiEvenPairWinMultiplier || '-'}`,
          `OP: ${rowData?.burfiOddPairWinMultiplier || '-'}`,
          `M: ${rowData?.burfiMatchWinMultiplier || '-'}`,
          `MM: ${rowData?.burfiMismatchWinMultiplier || '-'}`,
          `R: ${rowData?.burfiResultWinMultiplier || '-'}`
        ];
        return multipliers.join(' | ');
      }
    },
    {
      header: 'Barfi Single Commission', field: '',
      render: (rowData: GameConfig) => rowData?.burfiSingleCommissionRate || '-'
    },
    {
      header: 'Barfi Pair Commission', field: '',
      render: (rowData: GameConfig) => rowData?.burfiPairCommissionRate || '-'
    },
    {
      header: 'Barfi Match Commission', field: '',
      render: (rowData: GameConfig) => rowData?.burfiMatchCommissionRate || '-'
    },
    {
      header: 'Win Mulitplier Single',
      field: 'winMultiplierSingle'
    },
    {
      header: 'Win Mulitplier Panna',
      field: 'winMultiplierPatti'
    },
    {
      header: 'Digit1 (Min/Max)', field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.digit1Min; // Replace with actual field for Min value
        const max = rowData?.digit1Max; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Digit2 (Min/Max)', field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.digit2Min; // Replace with actual field for Min value
        const max = rowData?.digit2Max; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Digit3 (Min/Max)', field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.digit3Min; // Replace with actual field for Min value
        const max = rowData?.digit3Max; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Digit4 (Min/Max)', field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.digit4Min; // Replace with actual field for Min value
        const max = rowData?.digit4Max; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Digit5 (Min/Max)', field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.digit5Min; // Replace with actual field for Min value
        const max = rowData?.digit5Max; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    {
      header: 'Digit6 (Min/Max)', field: '',
      render: (rowData: GameConfig) => {
        const min = rowData?.digit6Min; // Replace with actual field for Min value
        const max = rowData?.digit6Max; // Replace with actual field for Max value
        return min && max ? `${min}/${max}` : '-'; // Return Min/Max or '-' if data is missing
      }
    },
    { header: 'Actions', field: 'actions' },
  ];



  const handleToggleStatus = (item: GameConfig) => {
    const response = window.confirm("Are you sure, you want to block the game config?");
    if (response) {
      dispatch(toggleGameConfigStatus(item.id))
        .unwrap()
        .catch((err: any) => {
          showModalStrip('danger', err, 5000);
        })
    }
  };

  const handleSaveGameConfig = (gameConfig: GameConfig) => {
    dispatch(saveGameConfig(gameConfig))
      .unwrap()
      .then(res => {
        setModalShow(false);
        setSelectedItem({} as GameConfig);
      })
      .catch((err: any) => {
        showModalStrip('danger', err, 5000);
      })
  }

  const handleEdit = (gameConfig: GameConfig) => {
    setSelectedItem(gameConfig);
    setModalShow(true);
  };

  return (
    <Container fluid>
      <PageHeading>Game Configurations</PageHeading>
      <AddButtonContainer>
        <SearchBox>
          <FormControl
            placeholder="Search by game name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBox>
        <Button
          variant="primary"
          onClick={() => {
            setSelectedItem({} as GameConfig);
            setModalShow(true);
          }}
        >
          Add New Game Configuration
        </Button>
      </AddButtonContainer>

      <TableWrapper>
        <GenericTable
          data={(searchQuery ? filteredData : gameConfigs?.content || []).map((gameConfig: GameConfig) => ({
            ...gameConfig,
            actions: (
              <ActionButtonContainer>
                <ActionIcon onClick={() => handleEdit(gameConfig)}>
                  <Edit />
                </ActionIcon>
                <ActionIcon onClick={() => handleToggleStatus(gameConfig)}>
                  <Ban color={gameConfig.status === 'ACTIVE' ? 'red' : 'green'} />
                </ActionIcon>
              </ActionButtonContainer>
            )
          }))}
          columns={columns}
          actions={false}
          pagination={true}
          paginationData={{
            totalElements: searchQuery ? filteredData.length : gameConfigs?.totalElements || 0,
            pageSize: paginationData.pageSize,
            currentPage: paginationData.currentPage
          }}
          onPageChange={(page: number, pageSize: number) => {
            // Convert to zero-based index for the API call
            setPaginationData(prev => ({
              ...prev,
              currentPage: page - 1, // Convert to zero-based for API
              pageSize
            }));
          }}
        />
      </TableWrapper>

      {/* Modal for adding or updating game config */}
      <GenericModal
        isOpen={modalShow}
        onClose={() => {
          setModalShow(false);
          setSelectedItem({} as GameConfig);
        }}
        node={
          <AddGameConfigPage
            data={selectedItem}
            onSave={handleSaveGameConfig}
          />
        }
        label={selectedItem?.id ? 'Update Game Config' : 'Add Game Config'}
      />
    </Container>
  );
};

export default GameConfigPage;
